import { createStore } from "vuex";

const searchUsers = (query, page) => {
    return fetch(`/api/user/search?q=${query}&page=${page || 0}`, {
        method: "GET",
        headers: {
            "content-type": "application/json"
        }
    });
};

const sharePlaylist = (playlistId, userId) => {
    return fetch(`/api/playlist/${playlistId}/share`, {
        method: "POST",
        body: JSON.stringify({ userId }),
        headers: {
            "content-type": "application/json"
        }
    });
};

const generateShareLink = (playlistId) => {
    return fetch(`/api/playlist/${playlistId}/share/link`, {
        method: "POST",
        headers: {
            "content-type": "application/json"
        }
    });
};

const getSharedData = (playlistId) => {
    return fetch(`/api/playlist/${playlistId}/share`, {
        method: "GET",
        headers: {
            "content-type": "application/json"
        }
    });
};

const updateRole = (playlistId, newRole) => {
    return fetch(`/api/playlist/${playlistId}/share/update`, {
        method: "PUT",
        body: JSON.stringify(newRole),
        headers: {
            "content-type": "application/json"
        }
    });
};

const removeUser = (playlistId, userId) => {
    return fetch(`/api/playlist/${playlistId}/share/user/${userId}`, {
        method: "DELETE",
        body: JSON.stringify({ userId }),
        headers: {
            "content-type": "application/json"
        }
    });
};

const shareWithTeam = (playlistId, teamId) => {
    return fetch(`/api/playlist/${playlistId}/share/team`, {
        method: "POST",
        body: JSON.stringify({ teamId }),
        headers: {
            "content-type": "application/json"
        }
    });
};

const removeTeam = (playlistId, teamId) => {
    return fetch(`/api/playlist/${playlistId}/share/team/${teamId}`, {
        method: "DELETE",
        body: JSON.stringify({ teamId }),
        headers: {
            "content-type": "application/json"
        }
    });
};

const store = createStore({
    state: {
        open: false,
        closed: true,
        modal: null,
        loadingLink: false,
        loadingSearch: false,
        loadingSharedUsers: false,
        loadingSharedTeams: false,
        error: false,
        message: "",
        searchText: "",
        searchResults: [],
        sharedUsers: [],
        sharedTeams: [],
        shareLink: "",
        currentPlaylistId: null,
        initialTab: 'share'
    },
    mutations: {
        OPEN(state, payload) {
            // Compat: string (playlistId) ou { playlistId, tab }
            const playlistId = typeof payload === 'string' ? payload : payload?.playlistId;
            const tab = (typeof payload === 'object' && payload?.tab) || 'share';
            if (state.modal) {
                state.modal.open();
                state.open = true;
                state.closed = !state.open;
                state.currentPlaylistId = playlistId;
                state.initialTab = tab;
                state.searchResults = [];
                state.shareLink = "";
                state.error = false;
                state.message = "";
            }
        },
        CLOSE(state) {
            if (state.modal) {
                state.modal.close();
                state.closed = true;
                state.open = !state.closed;
                state.currentPlaylistId = null;
                state.initialTab = 'share';
                state.searchResults = [];
                state.shareLink = "";
                state.error = false;
                state.message = "";
            }
        },
        SET_SEARCH_RESULTS(state, results) {
            state.searchResults = results;
        },
        SET_SHARE_LINK(state, link) {
            state.shareLink = link;
        },
        SET_ERROR(state, { error, message }) {
            state.error = error;
            state.message = message;
        },
        SET_SHARED_USERS(state, users) {
            state.sharedUsers = users;
        },
        SET_SHARED_TEAMS(state, teams) {
            state.sharedTeams = teams;
        },
        UPDATE_USER_ROLE(state, { userId, role }) {
            if (!userId) return;
            const index = state.sharedUsers.findIndex(u => (u.id || u._id) === userId);
            if (index !== -1) {
                state.sharedUsers[index] = { ...state.sharedUsers[index], role };
            }
        },
        UPDATE_TEAM_ROLE(state, { teamId, role }) {
            if (!teamId) return;
            const index = state.sharedTeams.findIndex(t => (t._id || t.id) === teamId);
            if (index !== -1) {
                state.sharedTeams[index] = { ...state.sharedTeams[index], role };
            }
        },
        REMOVE_USER(state, userId) {
            state.sharedUsers = state.sharedUsers.filter(u => (u.id || u._id) !== userId);
        },
        REMOVE_TEAM(state, teamId) {
            state.sharedTeams = state.sharedTeams.filter(t => (t._id || t.id) !== teamId);
        }
    },
    actions: {
        async open(context, payload) {
            context.commit("OPEN", payload);
            const playlistId = typeof payload === 'string' ? payload : payload?.playlistId;
            if (playlistId) {
                await context.dispatch("loadSharedEntities");
            }
        },
        close(context) {
            context.commit("CLOSE");
        },
        async searchUsers(context, { query, page }) {
            if (!query) {
                context.commit("SET_SEARCH_RESULTS", []);
                return;
            }
            
            context.state.loadingSearch = true;
            try {
                const response = await searchUsers(query, page);
                const results = await response.json();
                if (results.length > 0) {
                    context.commit("SET_SEARCH_RESULTS", results);
                } else {
                    context.commit("SET_ERROR", { 
                        error: true, 
                        message: "User not found" 
                    });
                    context.commit("SET_SEARCH_RESULTS", []);
                }
            } catch (error) {
                context.commit("SET_ERROR", { 
                    error: true, 
                    message: "Error searching users" 
                });
            } finally {
                context.state.loadingSearch = false;
            }
        },
        async shareWithUser(context, userId) {
            try {
                const response = await sharePlaylist(context.state.currentPlaylistId, userId);
                if (response.ok) {
                    context.commit("SET_ERROR", { 
                        error: false, 
                        message: "Playlist shared successfully" 
                    });
                } else {
                    const errData = await response.json().catch(() => ({}));
                    const msg = errData.message || (typeof errData.error === 'string' ? errData.error : null) || "Failed to share playlist";
                    throw new Error(msg);
                }
            } catch (error) {
                context.commit("SET_ERROR", { 
                    error: true, 
                    message: error.message || "Error sharing playlist" 
                });
            } 
        },
        async generateShareLink(context) {
            context.state.loadingLink = true;
            try {
                const response = await generateShareLink(context.state.currentPlaylistId);
                const data = await response.json().catch(() => ({}));
                if (response.ok && data.link) {
                    context.commit("SET_SHARE_LINK", data.link);
                } else {
                    const msg = data.message || (typeof data.error === 'string' ? data.error : null) || "Error generating share link";
                    throw new Error(msg);
                }
            } catch (error) {
                context.commit("SET_ERROR", { 
                    error: true, 
                    message: error.message || "Error generating share link" 
                });
            } finally {
                context.state.loadingLink = false;
            }
        },
        async loadSharedEntities(context) {
            if (!context.state.currentPlaylistId) return;
            
            context.state.loadingSharedUsers = true;
            context.state.loadingSharedTeams = true;
            context.state.sharedTeams = []
            context.state.sharedUsers = []
            try {
                const response = await getSharedData(context.state.currentPlaylistId);
                if (response.ok) {
                    const { users, teams } = await response.json();
                    context.commit("SET_SHARED_USERS", users || []);
                    context.commit("SET_SHARED_TEAMS", teams || []);
                } else {
                    const errData = await response.json().catch(() => ({}));
                    const msg = errData.message || (typeof errData.error === 'string' ? errData.error : null) || "Error loading shared data";
                    throw new Error(msg);
                }
            } catch (error) {
                context.commit("SET_ERROR", {
                    error: true,
                    message: error.message || "Error loading shared data"
                });
            } finally {
                context.state.loadingSharedUsers = false;
                context.state.loadingSharedTeams = false;
            }
        },
        async updateRole(context, newRole) {
            try {
                const response = await updateRole(context.state.currentPlaylistId, newRole);
                if (response.ok) {
                    context.commit("UPDATE_USER_ROLE", newRole);
                    context.commit("UPDATE_TEAM_ROLE", newRole);
                    context.commit("SET_ERROR", {
                        error: false,
                        message: "Role updated successfully"
                    });
                } else {
                    const errData = await response.json().catch(() => ({}));
                    const msg = errData.message || (typeof errData.error === 'string' ? errData.error : null) || "Failed to update role";
                    throw new Error(msg);
                }
            } catch (error) {
                // Reload shared entities to rollback UI dropdowns to actual state
                await context.dispatch("loadSharedEntities");
                context.commit("SET_ERROR", {
                    error: true,
                    message: error.message || "Error updating user role"
                });
            }
        },
        async removeUser(context, userId) {
            try {
                const response = await removeUser(context.state.currentPlaylistId, userId);
                if (response.ok) {
                    context.commit("REMOVE_USER", userId);
                    context.commit("SET_ERROR", {
                        error: false,
                        message: "User removed successfully"
                    });
                } else {
                    const errData = await response.json().catch(() => ({}));
                    const msg = errData.message || (typeof errData.error === 'string' ? errData.error : null) || "Failed to remove user";
                    throw new Error(msg);
                }
            } catch (error) {
                context.commit("SET_ERROR", {
                    error: true,
                    message: error.message || "Error removing user"
                });
            }
        },
        async shareWithTeam(context, teamId) {
            try {
                const response = await shareWithTeam(context.state.currentPlaylistId, teamId);
                if (response.ok) {
                    context.commit("SET_ERROR", { 
                        error: false, 
                        message: "Playlist shared with team successfully" 
                    });
                } else {
                    const errData = await response.json().catch(() => ({}));
                    const msg = errData.message || (typeof errData.error === 'string' ? errData.error : null) || "Failed to share playlist with team";
                    throw new Error(msg);
                }
            } catch (error) {
                context.commit("SET_ERROR", { 
                    error: true, 
                    message: error.message || "Error sharing playlist with team" 
                });
            } 
        },
        async removeTeam(context, teamId) {
            try {
                const response = await removeTeam(context.state.currentPlaylistId, teamId);
                if (response.ok) {
                    context.commit("REMOVE_TEAM", teamId);
                    context.commit("SET_ERROR", {
                        error: false,
                        message: "Team removed successfully"
                    });
                } else {
                    const errData = await response.json().catch(() => ({}));
                    const msg = errData.message || (typeof errData.error === 'string' ? errData.error : null) || "Failed to remove team";
                    throw new Error(msg);
                }
            } catch (error) {
                context.commit("SET_ERROR", {
                    error: true,
                    message: error.message || "Error removing team"
                });
            }
        }
    }
});

export default store; 